import { exerciseKey, resolveName } from "./exerciseKey.js";
import {
  cuesOutOfName,
  group,
  header,
  label,
  leadingSetsAndReps,
  leadingTarget,
  member,
  nestedDistance,
  sentenceCased,
  splitNote,
  stripListMarker,
  supersetHeader,
  supersetParts,
  trailingSetsAndReps,
  withoutHurdleMark,
} from "./ruleParser.js";
import type { Target } from "./models.js";
import { trimSet, trimWS } from "./text.js";
import type { BlockWire, LineReading, PlannedExerciseWire, TargetWire, WorkoutPlanWire } from "./types.js";

// DESIGN.md §32.12.2: where on one line of his text the reader found each part of what it made.
// The line is re-tokenized with the parser's own primitives and every span is checked against the
// reading before it is given, so a part the reader read differently gets no span rather than a wrong one.

export type SpanKind =
  | "bullet"
  | "sets"
  | "amount"
  | "name"
  | "effort"
  | "cue"
  | "note"
  | "rest"
  | "target"
  | "perSide"
  | "onlyFor"
  | "roundsSets"
  | "heading";

/** `start` and `end` are UTF-16 offsets into the line, as a textarea's selection counts them; `end` is exclusive. */
export interface LineSpan {
  start: number;
  end: number;
  kind: SpanKind;
  /** The exercise the part belongs to, for a part of one. */
  exerciseId?: string;
}

interface Found {
  exercise: PlannedExerciseWire;
  block: BlockWire;
}

interface Context {
  plan: WorkoutPlanWire;
  exercises: Map<string, Found>;
  known: readonly string[];
}

/**
 * The parts of `text`, one physical line of the definition read, that the reading `line` of `plan`
 * interpreted: disjoint, in order, within the line. `known` is what the line was read with; the
 * plan's own exercise names are always added to it.
 */
export function lineSpans(text: string, line: LineReading, plan: WorkoutPlanWire, known: readonly string[] = []): LineSpan[] {
  if (/[\n\r\u000B\u000C\u0085\u2028\u2029]/u.test(text)) return [];
  const exercises = new Map<string, Found>();
  for (const block of plan.blocks) for (const exercise of block.exercises) exercises.set(exercise.id, { exercise, block });
  const names = plan.blocks.flatMap((b) => b.exercises.map((e) => e.name));
  const context: Context = { plan, exercises, known: [...known, ...names] };

  const marker = listMarker(text);
  if (marker === null) return [];
  const spans: LineSpan[] = [];
  if (marker.bullet !== null) spans.push({ ...marker.bullet, kind: "bullet" });
  const body = { text: marker.body, start: marker.bodyStart };

  switch (line.kind) {
    case "blank":
      return [];
    case "heading":
      if (body.text !== "") spans.push({ start: body.start, end: body.start + body.text.length, kind: "heading" });
      break;
    case "block":
      spans.push(...headerSpans(body.text, body.start, blockOf(context, line.blockId)));
      break;
    case "note":
      spans.push(...noteSpans(body.text, body.start, line.exerciseIds[0], context));
      break;
    case "exercise":
    case "unread":
      spans.push(...exerciseLineSpans(body.text, body.start, line, context));
      break;
  }
  return tidy(spans, text.length);
}

// MARK: The line's marker

interface Marker {
  bullet: { start: number; end: number } | null;
  body: string;
  bodyStart: number;
}

const bulletChars = new Set(["-", "•", "*", "–", "—", "·", "▪", "‣", "+"]);
const space = /[\t\p{Zs}]/u;

/** `stripListMarker` on the trimmed line, with where its marker and body are. */
function listMarker(text: string): Marker | null {
  const expected = stripListMarker(trimWS(text));
  let i = 0;
  while (i < text.length && space.test(text[i]!)) i += 1;
  const start = i;
  while (i < text.length && bulletChars.has(text[i]!)) i += 1;
  let markerEnd = i;
  while (i < text.length && /\p{White_Space}/u.test(text[i]!)) i += 1;
  const numbered = /^\p{N}{1,2}[.)]\p{White_Space}+/u.exec(text.slice(i));
  if (numbered !== null) {
    markerEnd = i + numbered[0].trimEnd().length;
    i += numbered[0].length;
  }
  const raw = text.slice(i);
  const body = trimWS(raw);
  if (body !== expected.body) return null;
  return {
    bullet: expected.hadBullet && markerEnd > start ? { start, end: markerEnd } : null,
    body,
    bodyStart: i + raw.indexOf(body),
  };
}

// MARK: A rounds header

function blockOf(context: Context, id: string | undefined): BlockWire | undefined {
  return id === undefined ? undefined : context.plan.blocks.find((b) => b.id === id);
}

/** `3 sets (medium)`, `Superset x3`, `3-4 rounds`: the count where it says it, and its bracket. */
function headerSpans(body: string, at: number, block: BlockWire | undefined): LineSpan[] {
  if (block === undefined) return [];
  const found = supersetHeader(body) ?? header(body);
  if (found === null) return [];
  const out: LineSpan[] = [];
  const prefix = roundsPrefix(body);
  if (prefix !== null && found.sets !== null && block.sets === found.sets) out.push({ start: at + prefix.start, end: at + prefix.end, kind: "roundsSets" });
  if (found.remainder !== "") return out;
  const bracket = firstBracket(body);
  if (bracket !== null) {
    const content = trimWS(body.slice(bracket.open + 1, bracket.close));
    if (block.note !== undefined && hasPart(block.note, content)) {
      out.push({ start: at + bracket.open, end: at + bracket.end, kind: effortish(content) ? "effort" : "cue" });
    } else if (found.isSuperset && found.sets !== null && block.sets === found.sets && /\p{N}/u.test(content) && found.note === null) {
      out.push({ start: at + bracket.open, end: at + bracket.end, kind: "roundsSets" });
    }
  }
  return out;
}

const roundWord = /^(?:set|sets|round|rounds|rd|rds|circuit|circuits|series)$/iu;
const supersetWord = /^(?:superset|supersets|ss|s\/s)$/iu;

/** Where `3 sets`, `3x`, `3-4 rounds`, `Superset x3` or `3 supersets` is at the head of a header, or null. */
function roundsPrefix(body: string): { start: number; end: number } | null {
  const plain = /^(\p{N}{1,2}(?:[-–]\p{N}{1,2})?)\p{White_Space}*([xX×*]|\p{L}+)/u.exec(body);
  if (plain !== null && (/^[xX×*]$/u.test(plain[2]!) || roundWord.test(plain[2]!) || supersetWord.test(plain[2]!))) {
    let end = plain[0].length;
    if (supersetWord.test(plain[2]!)) {
      const after = /^\p{White_Space}*:?\p{White_Space}*[xX×*]?\p{White_Space}*\p{N}{1,2}/u.exec(body.slice(end));
      if (after !== null) end += after[0].length;
    }
    return { start: 0, end };
  }
  const superset = /^(?:superset|supersets|ss|s\/s)\b\p{White_Space}*:?\p{White_Space}*(?:[xX×*]\p{White_Space}*)?(\p{N}{1,2})?/iu.exec(body);
  if (superset !== null && superset[1] !== undefined) return { start: 0, end: superset[0].length };
  return null;
}

// MARK: A note line

function noteSpans(body: string, at: number, id: string | undefined, context: Context): LineSpan[] {
  const found = id === undefined ? undefined : context.exercises.get(id);
  if (found === undefined || body === "" || found.exercise.note === undefined) return [];
  return hasPart(found.exercise.note, body) ? [{ start: at, end: at + body.length, kind: "note", exerciseId: id }] : [];
}

// MARK: A line of exercises

interface Part {
  text: string;
  start: number;
}

/** One way the parser could have read the line: a lead (label, `3x(`, `3 sets:`) and the parts it read as exercises. */
interface Candidate {
  lead: LineSpan[];
  parts: Part[];
  /** Each part to its exercise id, in order. */
  ids: string[];
}

function exerciseLineSpans(body: string, at: number, line: LineReading, context: Context): LineSpan[] {
  const ids = line.exerciseIds.filter((id) => context.exercises.has(id));
  if (ids.length === 0 || body === "") return [];
  const block = blockOf(context, line.blockId) ?? context.exercises.get(ids[0]!)!.block;
  let best: LineSpan[] = [];
  let bestScore = -1;
  for (const candidate of candidates(body, at, ids, block, context)) {
    const spans = [...candidate.lead];
    candidate.parts.forEach((part, k) => {
      const id = candidate.ids[k]!;
      spans.push(...memberSpans(part, context.exercises.get(id)!, id, block, context));
    });
    const score = spans.filter((s) => s.kind !== "bullet").length;
    if (score > bestScore) {
      best = spans;
      bestScore = score;
    }
  }
  return best;
}

function* candidates(body: string, at: number, ids: string[], block: BlockWire, context: Context): Generator<Candidate> {
  const labelled = label(body);
  if (labelled !== null) {
    const restStart = body.indexOf(labelled.rest);
    if (restStart > 0) {
      const lead: LineSpan = { start: at, end: at + trimWS(body.slice(0, restStart)).length, kind: "bullet" };
      for (const c of membersOf(labelled.rest, at + restStart, ids, context)) yield { ...c, lead: [lead, ...c.lead] };
    }
  }

  const nested = nestedDistance(body);
  if (nested !== null && ids.length === nested.reps) {
    const restStart = body.lastIndexOf(nested.rest);
    if (restStart > 0 && restStart + nested.rest.length === body.length && block.sets === nested.sets) {
      const lead: LineSpan = { start: at, end: at + trimWS(body.slice(0, restStart)).length, kind: "roundsSets" };
      yield { lead: [lead], parts: [{ text: nested.rest, start: at + restStart }], ids: [ids[0]!] };
    }
  }

  const grouped = group(body);
  if (grouped !== null) {
    const open = body.indexOf("(");
    const contentStart = open + 1;
    if (open > 0 && body.startsWith(grouped.content, contentStart)) {
      const lead: LineSpan[] = block.sets === grouped.sets ? [{ start: at, end: at + trimWS(body.slice(0, open)).length, kind: "roundsSets" }] : [];
      for (const c of membersOf(grouped.content, at + contentStart, ids, context)) yield { ...c, lead: [...lead, ...c.lead] };
    }
  }

  const headed = supersetHeader(body) ?? header(body);
  if (headed !== null && headed.remainder !== "") {
    const restStart = body.lastIndexOf(headed.remainder);
    const prefix = roundsPrefix(body);
    if (restStart > 0 && restStart + headed.remainder.length === body.length) {
      const counted = prefix !== null && headed.sets !== null && block.sets === headed.sets;
      const sign = prefix !== null && /[xX×*]$/u.test(body.slice(prefix.start, prefix.end));
      const lead: LineSpan[] = counted ? [{ start: at + prefix!.start, end: at + prefix!.end, ...(sign && ids.length === 1 ? { kind: "sets" as const, exerciseId: ids[0]! } : { kind: "roundsSets" as const }) }] : [];
      for (const c of membersOf(headed.remainder, at + restStart, ids, context)) yield { ...c, lead: [...lead, ...c.lead] };
    }
  }

  yield* membersOf(body, at, ids, context);
}

/** `supersetParts` with where each part starts, when it makes as many exercises as the line did. */
function* membersOf(text: string, at: number, ids: string[], context: Context): Generator<Candidate> {
  const parts = supersetParts(text, context.known);
  if (parts.length !== ids.length) return;
  const located: Part[] = [];
  let cursor = 0;
  for (const part of parts) {
    const start = text.indexOf(part, cursor);
    if (start < 0) return;
    located.push({ text: part, start: at + start });
    cursor = start + part.length;
  }
  yield { lead: [], parts: located, ids };
}

/** The spans of one part `member()` read as `found.exercise`. */
function memberSpans(part: Part, found: Found, id: string, block: BlockWire, context: Context): LineSpan[] {
  const e = found.exercise;
  if (member(part.text, null, context.known) === null) return [];
  const t = trimWS(part.text);
  const base = part.start + part.text.indexOf(t);
  const out: LineSpan[] = [];
  const add = (start: number, end: number, kind: SpanKind) => {
    if (end > start) out.push({ start: base + start, end: base + end, kind, exerciseId: id });
  };
  /** `4x6` found at `where`: `4x` its sets, `6` its amount, each given if the reading has it. */
  const setsAndAmount = (where: { start: number; end: number }, sets: number, target: Target) => {
    const sign = /^\p{N}{1,2}\p{White_Space}*[xX×*]\p{White_Space}*/u.exec(t.slice(where.start, where.end));
    if (sign === null) return;
    if (setsMatch(sets, e, block)) add(where.start, where.start + sign[0].trimEnd().length, "sets");
    if (sameTarget(target, e.target)) add(where.start + sign[0].length, where.end, "amount");
  };

  const bracket = firstBracket(t);
  const split = splitNote(stripTrailingStar(t));
  if (bracket !== null && split.note !== null && trimWS(t.slice(bracket.open + 1, bracket.close)) === split.note && e.note !== undefined && hasPart(e.note, split.note)) {
    add(bracket.open, bracket.end, effortish(split.note) ? "effort" : "cue");
  }

  const rest = stripTrailingStar(split.rest);
  if (withoutHurdleMark(rest) !== null) return out;

  const outside = maskBrackets(t);
  const counts: { start: number; end: number }[] = [];
  let countEnd = 0;
  let nameText: string;
  let trailingCount: { text: string; sets: number; target: Target } | null = null;
  const leading = leadingSetsAndReps(rest);
  const target = leading === null ? leadingTarget(rest) : null;
  if (leading !== null || target?.target != null) {
    const restAfter = leading?.rest ?? target!.rest;
    const counted = trimWS(rest.slice(0, rest.length - restAfter.length));
    const where = locate(outside, counted, 0);
    if (where !== null && trimWS(outside.slice(0, where.start)) === "") {
      countEnd = where.end;
      counts.push(where);
      if (leading !== null) setsAndAmount(where, leading.sets, leading.target);
      else if (sameTarget(target!.target, e.target)) add(where.start, where.end, "amount");
    }
    nameText = restAfter;
  } else {
    const trailing = trailingSetsAndReps(rest);
    if (trailing !== null) {
      nameText = trailing.rest;
      trailingCount = { text: trimWS(rest.slice(trailing.rest.length)), sets: trailing.sets, target: trailing.target };
    } else {
      nameText = rest;
    }
  }

  let name = trimSet(nameText, " \t-–—:,;.");
  if (name.includes("*")) name = trimWS(name.split(" ").map((w) => (w.endsWith("*") ? w.slice(0, -1) : w)).join(" "));
  const lifted = cuesOutOfName(name);
  const cues = lifted.cues.slice();
  let cursor = countEnd;
  const leadCue = cues.length > 0 && name.toLowerCase().startsWith(`${cues[0]!} `) ? cues.shift()! : null;
  if (leadCue !== null) {
    const where = locate(outside, leadCue, cursor);
    if (where !== null) {
      if (e.note !== undefined && hasPart(e.note, leadCue)) add(where.start, where.end, "effort");
      cursor = where.end;
    }
  }
  let nameEnd = cursor;
  if (lifted.name !== "") {
    // His name can run on past a bracket (`lunge (knee down) position`): the name is the words either side.
    const where = locate(outside, lifted.name, cursor, true);
    if (where !== null) {
      if (sameName(lifted.name, e.name, context.known)) for (const piece of pieces(outside, where)) add(piece.start, piece.end, "name");
      nameEnd = where.end;
    }
  }
  for (const cue of cues) {
    const where = locateCue(outside, cue, nameEnd);
    if (where !== null && e.note !== undefined && hasPart(e.note, cue)) add(where.start, where.end, cueKind(cue, where.dashed));
  }
  if (trailingCount !== null) {
    const where = locate(outside, trailingCount.text, nameEnd);
    if (where !== null && where.end === trimEndLength(outside)) {
      counts.push(where);
      setsAndAmount(where, trailingCount.sets, trailingCount.target);
    }
  }

  if (e.perSide) {
    const last = trimEndLength(outside) - 1;
    for (let i = 0; i < t.length; i += 1) {
      if (outside[i] !== "*" || counts.some((c) => c.start <= i && i < c.end)) continue;
      const endsWord = i > 0 && !/\p{White_Space}/u.test(t[i - 1]!) && (i + 1 === t.length || /[\p{White_Space}(\[]/u.test(t[i + 1]!));
      if (i === last || endsWord) add(i, i + 1, "perSide");
    }
  }
  return out;
}

function stripTrailingStar(text: string): string {
  return text.endsWith("*") ? trimWS(text.slice(0, -1)) : text;
}

function trimEndLength(text: string): number {
  return text.replace(/[\p{White_Space}\u0001]+$/u, "").length;
}

/** The text with every bracket and what is inside it blanked, so a search never lands inside one. */
function maskBrackets(text: string): string {
  let depth = 0;
  let out = "";
  for (const c of text) {
    if (c === "(" || c === "[") {
      depth += 1;
      out += "\u0001".repeat(c.length);
    } else if (c === ")" || c === "]") {
      depth = Math.max(0, depth - 1);
      out += "\u0001".repeat(c.length);
    } else {
      out += depth > 0 ? "\u0001".repeat(c.length) : c;
    }
  }
  return out;
}

/** The first top-level bracket with something in it, as `splitNote` takes it: `end` past its close, or the line's end when unclosed. */
function firstBracket(text: string): { open: number; close: number; end: number } | null {
  let depth = 0;
  let open = -1;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i]!;
    if (c === "(" || c === "[") {
      depth += 1;
      if (depth === 1) open = i;
    } else if ((c === ")" || c === "]") && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        if (trimWS(text.slice(open + 1, i)) !== "") return { open, close: i, end: i + 1 };
        open = -1;
      }
    }
  }
  if (depth > 0 && open >= 0 && trimWS(text.slice(open + 1)) !== "") return { open, close: text.length, end: text.length };
  return null;
}

// MARK: Finding his words

const syntax = /[\\^$.*+?()[\]{}|/]/g;

/**
 * `needle` in `hay` from `from`, any run of white space matching any other, letters in any case;
 * `acrossBrackets`, a masked bracket may stand between two words, or be stuck to the end of one.
 */
function locate(hay: string, needle: string, from: number, acrossBrackets = false): { start: number; end: number } | null {
  const words = needle.split(/\p{White_Space}+/u).filter((w) => w !== "");
  if (words.length === 0) return null;
  const between = acrossBrackets ? String.raw`[\p{White_Space}\u0001]*?\p{White_Space}[\p{White_Space}\u0001]*|\u0001+\p{White_Space}*` : String.raw`\p{White_Space}+`;
  return search(hay, words.map((w) => w.replace(syntax, "\\$&")).join(`(?:${between})`), from);
}

/** The found words without the masked brackets inside them. */
function pieces(hay: string, where: { start: number; end: number }): { start: number; end: number }[] {
  const out: { start: number; end: number }[] = [];
  const run = /[^\u0001]+/gu;
  run.lastIndex = where.start;
  for (let match = run.exec(hay); match !== null && match.index < where.end; match = run.exec(hay)) {
    const text = hay.slice(match.index, Math.min(where.end, match.index + match[0].length));
    const start = match.index + (text.length - text.trimStart().length);
    const end = match.index + text.trimEnd().length;
    if (end > start) out.push({ start, end });
  }
  return out;
}

function search(hay: string, pattern: string, from: number): { start: number; end: number } | null {
  const regex = new RegExp(pattern, "giu");
  regex.lastIndex = from;
  const match = regex.exec(hay);
  return match === null ? null : { start: match.index, end: match.index + match[0].length };
}

/** A cue as he wrote it: `rest 2min` was `R: 2min`; one after a dash takes the dash with it. */
function locateCue(hay: string, cue: string, from: number): { start: number; end: number; dashed: boolean } | null {
  let where: { start: number; end: number } | null = null;
  if (cue.startsWith("rest ")) {
    const words = cue.slice(5).split(/\p{White_Space}+/u).filter((w) => w !== "");
    if (words.length > 0) where = search(hay, String.raw`R\p{White_Space}*:\p{White_Space}*` + words.map((w) => w.replace(syntax, "\\$&")).join(String.raw`\p{White_Space}+`), from);
  }
  where ??= locate(hay, cue, from);
  if (where === null) return null;
  const dash = /[-–—]\p{White_Space}*$/u.exec(hay.slice(from, where.start));
  if (dash !== null && /\p{White_Space}$/u.test(hay.slice(0, from + dash.index))) {
    return { start: from + dash.index, end: where.end, dashed: true };
  }
  return { ...where, dashed: false };
}

function cueKind(cue: string, dashed: boolean): SpanKind {
  if (cue.startsWith("rest ")) return "rest";
  if (/^target\b/iu.test(cue)) return "target";
  if (cue.startsWith("@") || /^to\p{White_Space}+\p{Nd}/u.test(cue)) return "effort";
  return dashed ? "note" : "cue";
}

// MARK: Checking against the reading

/** `part` is the whole of `note` or one of its ` · ` parts. */
function hasPart(note: string, part: string): boolean {
  return part !== "" && ` · ${note} · `.includes(` · ${part} · `);
}

function effortish(text: string): boolean {
  return /^(?:very\p{White_Space}+)?(?:light|medium|heavy)\b/iu.test(text) || /^@?\p{White_Space}*\p{Nd}+(?:\p{White_Space}*[-–]\p{White_Space}*\p{Nd}+)?\p{White_Space}*%/u.test(text);
}

function sameTarget(target: Target | null, wire: TargetWire | undefined): boolean {
  if (target === null || wire === undefined) return false;
  return target.amount === wire.amount && (target.upper ?? undefined) === wire.upper && target.unit === wire.unit;
}

function setsMatch(sets: number, e: PlannedExerciseWire, block: BlockWire): boolean {
  return e.groups === sets || (e.groups === undefined && block.sets === sets);
}

function sameName(written: string, name: string, known: readonly string[]): boolean {
  if (exerciseKey(written) === exerciseKey(name)) return true;
  return resolveName(sentenceCased(written), [name, ...known]) === name;
}

/** In order, within the line, none empty and none overlapping one before it. */
function tidy(spans: LineSpan[], length: number): LineSpan[] {
  const sorted = spans
    .filter((s) => s.start >= 0 && s.end <= length && s.end > s.start)
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const out: LineSpan[] = [];
  for (const span of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && span.start < last.end) continue;
    out.push(span);
  }
  return out;
}
